import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Stack,
  Grid,
  Divider,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Chat as ChatIcon,
  Tag as TagIcon,
  Notes as NotesIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Public as PublicIcon,
  FamilyRestroom as FamilyIcon,
  Cake as CakeIcon,
  AccessTime as TimeIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';

const statusColors = {
  New: 'info',
  Active: 'success',
  Inactive: 'default',
  Prospect: 'warning',
  Lost: 'error',
  Converted: 'success',
  Qualified: 'primary',
  Nurturing: 'secondary',
};

function ContactDetail({ contact, onEdit, onBack }) {
  const [activeTab, setActiveTab] = useState(0);
  if (!contact) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a contact to view details
        </Typography>
      </Box>
    );
  }
  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const getLeadScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };
  const getLeadScoreIcon = (score) => (score >= 80 ? <StarIcon /> : <StarBorderIcon />);
  function TabPanel({ children, value, index }) {
    return (
      <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Avatar src={contact.avatar} sx={{ width: 80, height: 80 }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h4" fontWeight={700}>{contact.name}</Typography>
              <Chip label={contact.status} color={statusColors[contact.status] || 'default'} size="medium" />
              <Chip label={contact.type || 'Contact'} variant="outlined" size="medium" />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body1" color="text.secondary">{contact.jobTitle}</Typography>
              {contact.company && (
              <>
                <Typography variant="body1" color="text.secondary">•</Typography>
                <Typography variant="body1" color="text.secondary">{contact.company}</Typography>
              </>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>Back</Button>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(contact)}>Edit</Button>
          </Stack>
        </Stack>
        {contact.leadScore && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              {getLeadScoreIcon(contact.leadScore)}
              <Typography variant="body2" fontWeight={600}>
                Lead Score:
                {contact.leadScore}
                /100
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={contact.leadScore} color={getLeadScoreColor(contact.leadScore)} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}
      </Paper>
      <Paper elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="contact tabs">
            <Tab label="Overview" />
            <Tab label="Contact Info" />
            <Tab label="CRM Details" />
            <Tab label="Personal" />
            <Tab label="Timeline" />
            <Tab label="Notes" />
            <Tab label="Files" />
            <Tab label="Tasks" />
            <Tab label="Activity" />
          </Tabs>
        </Box>
        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Quick Stats</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CalendarIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Created" secondary={new Date(contact.createdAt).toLocaleDateString()} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CalendarIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Last Updated" secondary={new Date(contact.updatedAt).toLocaleDateString()} />
                    </ListItem>
                    {contact.source && (
                    <ListItem>
                      <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Source" secondary={contact.source} />
                    </ListItem>
                    )}
                    {contact.referredBy && (
                    <ListItem>
                      <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Referred By" secondary={contact.referredBy} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Tags</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(contact.tags || []).map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" icon={<TagIcon />} />
                    ))}
                    {(!contact.tags || contact.tags.length === 0) && <Typography variant="body2" color="text.secondary">No tags assigned</Typography>}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            {contact.aiIntent && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>AI Intent Analysis</Typography>
                    <Typography variant="body1" color="text.secondary">{contact.aiIntent}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        {/* Contact Info Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Email" secondary={contact.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Phone" secondary={contact.phone} />
                    </ListItem>
                    {contact.website && (
                    <ListItem>
                      <ListItemIcon><PublicIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Website" secondary={<a href={contact.website} target="_blank" rel="noopener noreferrer">{contact.website}</a>} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Professional Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Company" secondary={contact.company} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WorkIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Job Title" secondary={contact.jobTitle} />
                    </ListItem>
                    {contact.linkedin && (
                    <ListItem>
                      <ListItemIcon><LinkedInIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="LinkedIn" secondary={<a href={contact.linkedin} target="_blank" rel="noopener noreferrer">View Profile</a>} />
                    </ListItem>
                    )}
                    {contact.twitter && (
                    <ListItem>
                      <ListItemIcon><TwitterIcon color="primary" /></ListItemIcon>
                      <ListItemText
                        primary="Twitter"
                        secondary={(
                          <a href={contact.twitter} target="_blank" rel="noopener noreferrer">
                            @
                            {contact.twitter.replace('@', '')}
                          </a>
)}
                      />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            {contact.address && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Address</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationIcon color="primary" />
                      <Typography variant="body1">{contact.address}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        {/* CRM Details Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Sales Information</Typography>
                  <List dense>
                    {contact.budget && (
                    <ListItem>
                      <ListItemIcon><MoneyIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Budget" secondary={contact.budget} />
                    </ListItem>
                    )}
                    {contact.timeline && (
                    <ListItem>
                      <ListItemIcon><TimelineIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Timeline" secondary={contact.timeline} />
                    </ListItem>
                    )}
                    {contact.preferredZip && (
                    <ListItem>
                      <ListItemIcon><LocationIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Preferred Zip" secondary={contact.preferredZip} />
                    </ListItem>
                    )}
                    {contact.communication && (
                    <ListItem>
                      <ListItemIcon><ChatIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Preferred Communication" secondary={contact.communication} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Lead Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><StarIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Lead Score" secondary={`${contact.leadScore || 0}/100`} />
                    </ListItem>
                    {contact.stage && (
                    <ListItem>
                      <ListItemIcon><WorkIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Stage" secondary={contact.stage} />
                    </ListItem>
                    )}
                    {contact.interests && (
                    <ListItem>
                      <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Interests" secondary={contact.interests} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        {/* Personal Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <List dense>
                    {contact.birthday && (
                    <ListItem>
                      <ListItemIcon><CakeIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Birthday" secondary={new Date(contact.birthday).toLocaleDateString()} />
                    </ListItem>
                    )}
                    {contact.spouse && (
                    <ListItem>
                      <ListItemIcon><FamilyIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Spouse" secondary={contact.spouse} />
                    </ListItem>
                    )}
                    {contact.children && (
                    <ListItem>
                      <ListItemIcon><FamilyIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Children" secondary={contact.children} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Preferences</Typography>
                  <List dense>
                    {contact.language && (
                    <ListItem>
                      <ListItemIcon><TranslateIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Language" secondary={contact.language} />
                    </ListItem>
                    )}
                    {contact.timezone && (
                    <ListItem>
                      <ListItemIcon><TimeIcon color="primary" /></ListItemIcon>
                      <ListItemText primary="Timezone" secondary={contact.timezone} />
                    </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        {/* Timeline Tab */}
        <TabPanel value={activeTab} index={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Timeline</Typography>
              <Typography variant="body2" color="text.secondary">Timeline and activity will be shown here.</Typography>
            </CardContent>
          </Card>
        </TabPanel>
        {/* Notes Tab */}
        <TabPanel value={activeTab} index={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              {contact.notes ? (<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{contact.notes}</Typography>) : (<Typography variant="body2" color="text.secondary">No notes available</Typography>)}
            </CardContent>
          </Card>
        </TabPanel>
        {/* Files Tab */}
        <TabPanel value={activeTab} index={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Files</Typography>
              <Typography variant="body2" color="text.secondary">Files and attachments will be shown here.</Typography>
            </CardContent>
          </Card>
        </TabPanel>
        {/* Tasks Tab */}
        <TabPanel value={activeTab} index={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tasks</Typography>
              <Typography variant="body2" color="text.secondary">Tasks and reminders will be shown here.</Typography>
            </CardContent>
          </Card>
        </TabPanel>
        {/* Activity Tab */}
        <TabPanel value={activeTab} index={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Activity</Typography>
              <Typography variant="body2" color="text.secondary">Recent activity and logs will be shown here.</Typography>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default ContactDetail;
